import { Paper, Grid } from '@material-ui/core';
import CustomCheckbox from 'components/CustomCheckbox';
import TextHeader from 'components/CustomText/TextHeader';
import CustomTextField from 'components/Input/CustomTextField';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import NumberListFormItem from './NumberListFormItem';
import CustomTable from 'components/CustomTable';
import CustomButton from 'components/CustomInput/Button';
import PopupInput from 'components/Application/TeamMemberForm/PopupInput';
import useSourceFundingData from 'hooks/tableData/useSourceFundingData';
import useCollaboratingPartyData from 'hooks/tableData/useCollaboratingPartyData';
import useNonMonetaryData from 'hooks/tableData/useNonMonetaryData';
import { initializeTableData, produce, isHighlight as isHL } from 'utility/general';
import DataListener from './DataListener';
import { useDispatch, useSelector } from 'react-redux';
import { SET_SITE_VALIDATION, SET_STUDY_VALIDATION, UPDATE_FORM_VALIDITY } from 'store/key';
import { getStore } from 'store';

const SPaperLayout = styled.div`
    padding: 10px;
`;

const SLayout = styled.div`
    padding: 10px;
`;

export default function BudgetFormLayout({
    tabType,
    formId,
    defaultData = {},
    disableOnPermission = false,
    disableTableOperations = false,
    onFormUpdate = (key, value) => null,
    applicationDetail = {},
    siteInfoId,
    isAmendmentEdit = false,
    isAmendmentCreate = false,
    onSiteInfoUpdate = (siteInfoId, key, value) => null,
    applicationCreateDtm,
    allComments,
    getCommentDetail,
    isEnableComment,
}) {
    const {
        openPopup: openFundPopup,
        setOpenPopup: setFundOpenPopup,
        columns: fundColumns,
        rows: fundRows,
        setRows: setFundRows,
        defaultFormList: defaultFundFormList,
        selectedRow: selectedFundRow,
        handleRowCreate: handleFundRowCreate,
        onRecordUpdate: handleFundRecordUpdate,
        handleEditClick: handleFundEditClick,
        handleDelete: handleFundDelete,
        onEditSubmit: onFundEditSubmit,
        onDataEmpty: onFundDataEmpty,
        recoverDataCreation: recoverFundDataCreation,
        removeAllTempData: removeAllSourceFundingTempData,
    } = useSourceFundingData({ formId });

    const {
        openPopup: openNonMonetaryPopup,
        setOpenPopup: setNonMonetaryOpenPopup,
        columns: nonMonetaryColumns,
        rows: nonMonetaryRows,
        setRows: setNonMonetaryRows,
        defaultFormList: defaultNonMonetaryFormList,
        selectedRow: selectedNonMonetaryRow,
        handleRowCreate: handleNonMonetaryRowCreate,
        onRecordUpdate: handleNonMonetaryRecordUpdate,
        handleEditClick: handleNonMonetaryEditClick,
        handleDelete: handleNonMonetaryDelete,
        onEditSubmit: onMonMonetaryEditSubmit,
        onDataEmpty: onNonMonetaryDataEmpty,
        recoverDataCreation: recoverNonMonetaryDataCreation,
        removeAllTempData: removeAllNonMonetaryTempData,
    } = useNonMonetaryData({ formId });

    const {
        openPopup: openCollaboratePopup,
        setOpenPopup: setCollaborateOpenPopup,
        columns: collaborateColumns,
        rows: collaborateRows,
        setRows: setCollaborateRows,
        defaultFormList: defaultCollaborateFormList,
        selectedRow: selectedCollaborateRow,
        handleRowCreate: handleCollaborateRowCreate,
        onRecordUpdate: handleCollaborateRecordUpdate,
        handleEditClick: handleCollaborateEditClick,
        handleDelete: handleCollaborateDelete,
        onEditSubmit: onCollaborateEditSubmit,
        removeAllTempData: removeAllCollaboratingTempData,
    } = useCollaboratingPartyData({ formId });

    const [postData, setPostData] = useState({});
    const [sitePostData, setSitePostData] = useState({});

    const [studyInfoValidation, setStudyInfoValidation] = useState({});
    const [siteInfoValidation, setSiteInfoValidation] = useState({});

    const validationKey = 'budgetResources';

    const isStudyValid = useSelector(
        (state) => state.applicationDetailValid.isApplicationFormDetailValid?.[validationKey],
    );

    const isSiteValid = useSelector(
        (state) => state.applicationDetailValid.siteInfoValid?.[siteInfoId]?.[validationKey],
    );

    const dispatch = useDispatch();
    const isAmendment = isAmendmentEdit || isAmendmentCreate;

    // initialize the study-info postData if edit data exist
    useEffect(() => {
        if (tabType === 'info') {
            const { applicationDetailPostData } = getStore().getState();
            const postData = applicationDetailPostData.postData?.applicationForm ?? {};
            setPostData(postData);

            const fundingData = initializeTableData({
                tableKey: 'funding',
                currentTableData: fundRows,
            });

            setFundRows(fundingData);

            const supportData = initializeTableData({
                tableKey: 'support',
                currentTableData: nonMonetaryRows,
            });

            setNonMonetaryRows(supportData);

            const collaboratingData = initializeTableData({
                tableKey: 'collaborating',
                currentTableData: collaborateRows,
            });

            setCollaborateRows(collaboratingData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // initialize the postData if edit exist
    useEffect(() => {
        if (tabType === 'hospital') {
            const { applicationDetailPostData } = getStore().getState();
            const siteInfo = applicationDetailPostData.postData.siteInfo;
            const currentSiteInfoPostData = siteInfo?.find((item) => item.id === siteInfoId);
            setSitePostData(currentSiteInfoPostData ? { [siteInfoId]: currentSiteInfoPostData } : {});
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // update study-info validity to the reducer
    useEffect(() => {
        const validations = Object.values(studyInfoValidation);

        // data is not initialized
        if (validations.length === 0) {
            return;
        }

        const currentStatus = validations.every((item) => item);

        if (currentStatus !== isStudyValid) {
            dispatch({
                type: UPDATE_FORM_VALIDITY,
                payload: {
                    type: 'study',
                    applicationFormId: formId,
                    formKey: validationKey,
                    isValid: currentStatus,
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    // update site-info validity to the reducer
    useEffect(() => {
        const validations = Object.values(siteInfoValidation);

        // data is not initialized
        if (validations.length === 0) {
            return;
        }

        const currentStatus = validations.every((item) => item);

        if (currentStatus !== isSiteValid) {
            dispatch({
                type: UPDATE_FORM_VALIDITY,
                payload: {
                    type: 'site',
                    applicationFormId: formId,
                    siteInfoId: siteInfoId,
                    formKey: validationKey,
                    isValid: currentStatus,
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteInfoValidation]);

    const updateStudyValidation = (name, isValidate) => {
        setStudyInfoValidation(
            produce((draft) => {
                draft[name] = isValidate;
            }),
        );
    };

    const updateSiteValidation = (name, isValidate) => {
        setSiteInfoValidation(
            produce((draft) => {
                draft[name] = isValidate;
            }),
        );
    };

    // when studyInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_STUDY_VALIDATION,
            payload: {
                [validationKey]: studyInfoValidation,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    // when siteInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_SITE_VALIDATION,
            payload: {
                siteInfoId,
                [validationKey]: siteInfoValidation,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteInfoValidation]);

    const getData = (key) => {
        return postData[key] ?? defaultData[key];
    };

    const isValueExistInField = (fieldKey, allowedKeys = []) => {
        const data = getData(fieldKey);

        if (allowedKeys.includes(data)) {
            return true;
        }
        return false;
    };

    const onFieldChange = (e) => {
        const { name, value } = e.target;

        setPostData((prevData) => {
            return {
                ...prevData,
                [name]: value,
            };
        });

        // setPostData(
        //     produce((draft) => {
        //         draft[name] = value;
        //     }),
        // );

        onFormUpdate(name, value);
    };

    const getDefaultData = (key) => {
        const site = applicationDetail.siteInfo.find((item) => {
            return item.id === siteInfoId;
        });

        return site?.[key];
    };

    const getSiteData = (key) => {
        return sitePostData?.[siteInfoId]?.[key] ?? getDefaultData(key);
    };

    const onSiteFieldChange = (e) => {
        const { value, name } = e.target;

        setSitePostData((prevData) => {
            const newData = produce(prevData, (draft) => {
                if (!draft[siteInfoId]) {
                    draft[siteInfoId] = {};
                }

                draft[siteInfoId][name] = value;
            });

            return newData;
        });

        onSiteInfoUpdate(siteInfoId, name, value);
    };

    const isHighlight = (keys, istable = false) => {
        return isHL(applicationDetail, siteInfoId, formId, tabType, keys, istable);
    };

    return (
        <SPaperLayout>
            <DataListener
                onPostDataReset={() => {
                    setPostData({});
                    setSitePostData({});
                    removeAllCollaboratingTempData();
                    removeAllNonMonetaryTempData();
                    removeAllSourceFundingTempData();
                }}
            />
            {openFundPopup && (
                <PopupInput
                    title={'Specify the source of funding'}
                    height="500px"
                    width="700px"
                    alignItems={'center'}
                    defaultFormList={defaultFundFormList}
                    selectedRow={selectedFundRow}
                    editRow={onFundEditSubmit}
                    setOpenPopup={setFundOpenPopup}
                    tableList={fundRows}
                    onSubmit={handleFundRecordUpdate}
                />
            )}

            {openNonMonetaryPopup && (
                <PopupInput
                    title={'Specify the non-monetary support'}
                    height="450px"
                    width="700px"
                    alignItems={'center'}
                    defaultFormList={defaultNonMonetaryFormList}
                    selectedRow={selectedNonMonetaryRow}
                    editRow={onMonMonetaryEditSubmit}
                    setOpenPopup={setNonMonetaryOpenPopup}
                    tableList={nonMonetaryRows}
                    onSubmit={(data) => handleNonMonetaryRecordUpdate(data)}
                />
            )}

            {openCollaboratePopup && (
                <PopupInput
                    title={'Collaborating parties that jointly take on the responsibilities for the study'}
                    height="250px"
                    width="700px"
                    alignItems={'center'}
                    defaultFormList={defaultCollaborateFormList}
                    selectedRow={selectedCollaborateRow}
                    editRow={onCollaborateEditSubmit}
                    setOpenPopup={setCollaborateOpenPopup}
                    tableList={collaborateRows}
                    onSubmit={(data) => handleCollaborateRecordUpdate(data)}
                />
            )}

            <Paper>
                <SLayout>
                    <TextHeader underline italic>
                        PART IV: BUDGET, RESOURCES, INDEMNITY AND INSURANCE
                    </TextHeader>
                    {tabType === 'hospital' ? (
                        <>
                            <NumberListFormItem
                                disableOnPermission={disableOnPermission}
                                key={`p4ExtraHAResources${siteInfoId}`}
                                number={'19.1'}
                                label={'Will this study consume extra HA resources?'}
                                showTrueFalse
                                trueFalseValue={getSiteData('p4ExtraHAResources')}
                                onAppFieldValidate={updateSiteValidation}
                                name={'p4ExtraHAResources'}
                                onTrueFalseSelected={onSiteFieldChange}
                                highlight={isHighlight(['p4ExtraHAResources'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.1 Will this study consume extra HA resources?', 
                                    commentField: 'p4ExtraHAResources',
                                    commentDetail: getCommentDetail("p4ExtraHAResources", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'19.1.1'}
                                level={2}
                                highlight={isHighlight(['p4ExtraHAResourcesDetails'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.1.1 If yes, provide details', 
                                    commentField: 'p4ExtraHAResourcesDetails',
                                    commentDetail: getCommentDetail("p4ExtraHAResourcesDetails", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    key={`p4ExtraHAResourcesDetails${siteInfoId}`}
                                    debounceChange
                                    label={'If yes, provide details'}
                                    value={getSiteData('p4ExtraHAResourcesDetails')}
                                    name={'p4ExtraHAResourcesDetails'}
                                    onAppFieldValidate={updateSiteValidation}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    onChange={onSiteFieldChange}
                                    allowAction={getSiteData('p4ExtraHAResources') === true}
                                    updateApplicationFormOnDisableChange={formId}
                                    updateSiteFormOnDisableChange={siteInfoId}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'19.1.2'}
                                level={2}
                                label={
                                    'If extra HA resources is required, how will this affect the HA services of other patients with competing needs?'
                                }
                                displayContentNextLine
                                highlight={isHighlight(['p4ExtraHAResourcesAffect'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.1.2 If extra HA resources is required, how will this affect the HA services of other patients with competing needs?', 
                                    commentField: 'p4ExtraHAResourcesAffect',
                                    commentDetail: getCommentDetail("p4ExtraHAResourcesAffect", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    key={`p4ExtraHAResourcesAffect${siteInfoId}`}
                                    // style={{ width: 500 }}
                                    debounceChange
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                    onAppFieldValidate={updateSiteValidation}
                                    value={getSiteData('p4ExtraHAResourcesAffect')}
                                    name={'p4ExtraHAResourcesAffect'}
                                    onChange={onSiteFieldChange}
                                    allowAction={getSiteData('p4ExtraHAResources') === true}
                                    updateApplicationFormOnDisableChange={formId}
                                    updateSiteFormOnDisableChange={siteInfoId}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                disableOnPermission={disableOnPermission}
                                key={`p4Reimburse${siteInfoId}`}
                                number={'19.2'}
                                showTrueFalse
                                onAppFieldValidate={updateSiteValidation}
                                label={'Will the study site (hospital) receive reimbursement for the study?'}
                                trueFalseValue={getSiteData('p4Reimburse')}
                                name={'p4Reimburse'}
                                onTrueFalseSelected={onSiteFieldChange}
                                highlight={isHighlight(['p4Reimburse'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.2 Will the study site (hospital) receive reimbursement for the study?', 
                                    commentField: 'p4Reimburse',
                                    commentDetail: getCommentDetail("p4Reimburse", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'19.2.1'}
                                level={2}
                                highlight={isHighlight(['p4ReimburseFormat'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.2.1 If yes, state the format of reimbursement', 
                                    commentField: 'p4ReimburseFormat',
                                    commentDetail: getCommentDetail("p4ReimburseFormat", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    key={`p4ReimburseFormat${siteInfoId}`}
                                    value={getSiteData('p4ReimburseFormat')}
                                    name={'p4ReimburseFormat'}
                                    debounceChange
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    onAppFieldValidate={updateSiteValidation}
                                    onChange={onSiteFieldChange}
                                    label={'If yes, state the format of reimbursement'}
                                    allowAction={getSiteData('p4Reimburse') === true}
                                    updateApplicationFormOnDisableChange={formId}
                                    updateSiteFormOnDisableChange={siteInfoId}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'19.2.2'}
                                level={2}
                                highlight={isHighlight(['p4ReimburseReason'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.2.2 If no, state the reason(s)', 
                                    commentField: 'p4ReimburseReason',
                                    commentDetail: getCommentDetail("p4ReimburseReason", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    key={`p4ReimburseReason${siteInfoId}`}
                                    label={'If no, state the reason(s)'}
                                    debounceChange
                                    value={getSiteData('p4ReimburseReason')}
                                    name={'p4ReimburseReason'}
                                    onAppFieldValidate={updateSiteValidation}
                                    maxLength={1000}
                                    onChange={onSiteFieldChange}
                                    allowAction={getSiteData('p4Reimburse') === false}
                                    updateApplicationFormOnDisableChange={formId}
                                    updateSiteFormOnDisableChange={siteInfoId}
                                />
                            </NumberListFormItem>
                        </>
                    ) : (
                        <>
                            <NumberListFormItem number={'18'} bold label={`Source of Funding`} fontSize={16} />
                            <NumberListFormItem
                                number={'18.1'}
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                label={`Any source of funding?`}
                                trueFalseValue={getData('p4Funding')}
                                name={'p4Funding'}
                                onAppFieldValidate={updateStudyValidation}
                                onTrueFalseSelected={(e) => {
                                    onFieldChange(e);

                                    if (e.target.value) {
                                        recoverFundDataCreation();
                                    } else {
                                        onFundDataEmpty();
                                    }
                                }}
                                highlight={isHighlight(['p4Funding'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '18.1 Any source of funding?', 
                                    commentField: 'p4Funding',
                                    commentDetail: getCommentDetail("p4Funding", allComments),
                                }}
                            />
                            <NumberListFormItem
                                number={'18.1.1'}
                                level={2}
                                displayContentNextLine
                                label={`Specify the source of funding`}
                                highlight={isHighlight(['funding'], true)}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '18.1.1 Specify the source of funding', 
                                    commentStatus: null,
                                    commentField: 'funding',
                                    commentDetail: getCommentDetail("funding", allComments),
                                }}
                            >
                                <Grid container>
                                    <CustomButton
                                        allowAction={getData('p4Funding') === true}
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        style={{ marginBottom: 8 }}
                                        onClick={handleFundRowCreate}
                                    >
                                        Add
                                    </CustomButton>
                                    <CustomTable
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        isEditAllow
                                        isDeleteAllow
                                        allowAction={getData('p4Funding') === true}
                                        onDelete={handleFundDelete}
                                        onEdit={handleFundEditClick}
                                        disableOldDataDelete={isAmendment}
                                        name={'funding'}
                                        onAppFieldValidate={updateStudyValidation}
                                        fullWidthTable
                                        rows={fundRows}
                                        columns={fundColumns}
                                        applicationCreateDtm={applicationCreateDtm}
                                    />
                                </Grid>
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'18.2'}
                                label={`Any non-monetary support?`}
                                disableOnPermission={disableOnPermission}
                                showTrueFalse
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p4NonMonetary')}
                                name={'p4NonMonetary'}
                                onTrueFalseSelected={(e) => {
                                    onFieldChange(e);
                                    if (e.target.value) {
                                        recoverNonMonetaryDataCreation();
                                    } else {
                                        onNonMonetaryDataEmpty();
                                    }
                                }}
                                highlight={isHighlight(['p4NonMonetary'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '18.2 Any non-monetary support?', 
                                    commentField: 'p4NonMonetary',
                                    commentDetail: getCommentDetail("p4NonMonetary", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'18.2.1'}
                                label={`Specify the non-monetary support`}
                                level={2}
                                displayContentNextLine
                                highlight={isHighlight(['support'], true)}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '18.2.1 Specify the non-monetary support', 
                                    commentStatus: null,
                                    commentField: 'support',
                                    commentDetail: getCommentDetail("support", allComments),
                                }}
                            >
                                <Grid container>
                                    <CustomButton
                                        allowAction={getData('p4NonMonetary') === true}
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        style={{ marginBottom: 8 }}
                                        onClick={handleNonMonetaryRowCreate}
                                    >
                                        Add
                                    </CustomButton>
                                    <CustomTable
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        isEditAllow
                                        isDeleteAllow
                                        fullWidthTable
                                        name={'support'}
                                        disableOldDataDelete={isAmendment}
                                        onAppFieldValidate={updateStudyValidation}
                                        allowAction={getData('p4NonMonetary') === true}
                                        onDelete={handleNonMonetaryDelete}
                                        onEdit={handleNonMonetaryEditClick}
                                        rows={nonMonetaryRows}
                                        columns={nonMonetaryColumns}
                                        applicationCreateDtm={applicationCreateDtm}
                                    />
                                </Grid>
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'18.2.2'}
                                alignItems={'flex-start'}
                                level={2}
                                highlight={isHighlight(['p4SupportArrange'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '18.2.2 Please provide details arrangements of the non-monetary support', 
                                    commentField: 'p4SupportArrange',
                                    commentDetail: getCommentDetail("p4SupportArrange", allComments),
                                }}
                            >
                                <CustomTextField
                                    value={getData('p4SupportArrange')}
                                    disableOnPermission={disableOnPermission}
                                    name={'p4SupportArrange'}
                                    maxLength={255}
                                    isTextArea
                                    textAreaMinRow={4}
                                    debounceChange
                                    onChange={onFieldChange}
                                    onAppFieldValidate={updateStudyValidation}
                                    allowAction={getData('p4NonMonetary') === true}
                                    updateApplicationFormOnDisableChange={formId}
                                    label={'Please provide details arrangements of the non-monetary support'}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'18.3'}
                                disableOnPermission={disableOnPermission}
                                labelTooltip={
                                    'ICH GCP Guideline defines “sponsor” as an individual, company, institution, or organization which takes responsibility for the initiation, management, and/or financing of a clinical trial.'
                                }
                                showTrueFalse
                                label={'Is it a sponsored study?'}
                                trueFalseValue={getData('p4Sponsor')}
                                onAppFieldValidate={updateStudyValidation}
                                name={'p4Sponsor'}
                                onTrueFalseSelected={(e) => {
                                    onFieldChange(e);
                                    if (e.target.value === false) {
                                        setPostData(
                                            produce((draft) => {
                                                draft['p4SponsorType'] = null;
                                            }),
                                        );
                                        onFormUpdate('p4SponsorType', null);
                                    }
                                }}
                                highlight={isHighlight(['p4Sponsor'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '18.3 Is it a sponsored study?', 
                                    commentField: 'p4Sponsor',
                                    commentDetail: getCommentDetail("p4Sponsor", allComments),
                                }}
                            />
                            <NumberListFormItem
                                number={'18.3.1'}
                                displayContentNextLine
                                label={`Which type of sponsored study?`}
                                level={2}
                                highlight={isHighlight(['p4SponsorType'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '18.3.1 Which type of sponsored study?', 
                                    commentField: 'p4SponsorType',
                                    commentDetail: getCommentDetail("p4SponsorType", allComments),
                                }}
                            >
                                <CustomCheckbox
                                    radioButton
                                    allowAction={getData('p4Sponsor') === true}
                                    disableOnPermission={disableOnPermission}
                                    onAppFieldValidate={updateStudyValidation}
                                    updateApplicationFormOnDisableChange={formId}
                                    defaultData={[
                                        {
                                            key: true,
                                            value: getData('p4SponsorType') === true && getData('p4Sponsor') === true,
                                            display: 'Commercial-sponsored study',
                                        },
                                        {
                                            key: false,
                                            value: getData('p4SponsorType') === false && getData('p4Sponsor') === true,
                                            display: 'Non-commercial-sponsored study',
                                        },
                                    ]}
                                    name={'p4SponsorType'}
                                    value={getData('p4Sponsor') === true ? [getData('p4SponsorType')] : []}
                                    onChange={(name, value) => {
                                        if (getData('p4Sponsor') !== true) return;
                                        const selectedObject = value.find((item) => item.value);
                                        setPostData(
                                            produce((draft) => {
                                                draft[name] = selectedObject ? selectedObject.key : null;
                                            }),
                                        );
                                        onFormUpdate(name, selectedObject ? selectedObject.key : null);
                                    }}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'19'}
                                bold
                                label={'Resources Implication and Conflict of Interest'}
                                fontSize={16}
                                style={{ marginTop: 30 }}
                            />
                            <NumberListFormItem
                                number={'19.1'}
                                label={'Will this study consume extra HA resources?'}
                                showTrueFalse
                                disableOnPermission
                                trueFalseValue={getData('p4ExtraHAResources')}
                                name={'p4ExtraHAResources'}
                                onTrueFalseSelected={onFieldChange}
                                showDisableMessage
                                highlight={isHighlight(['p4ExtraHAResources'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.1 Will this study consume extra HA resources?', 
                                    commentField: 'p4ExtraHAResources',
                                    commentDetail: getCommentDetail("p4ExtraHAResources", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'19.1.1'}
                                level={2}
                                highlight={isHighlight(['p4ExtraHAResourcesDetails'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.1.1 If yes, provide details', 
                                    commentField: 'p4ExtraHAResourcesDetails',
                                    commentDetail: getCommentDetail("p4ExtraHAResourcesDetails", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    label={'If yes, provide details'}
                                    value={getData('p4ExtraHAResourcesDetails')}
                                    name={'p4ExtraHAResourcesDetails'}
                                    onChange={onFieldChange}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    disableOnPermission
                                    updateApplicationFormOnDisableChange={formId}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'19.1.2'}
                                level={2}
                                label={
                                    'If extra HA resources is required, how will this affect the HA services of other patients with competing needs?'
                                }
                                displayContentNextLine
                                showDisableMessage
                                highlight={isHighlight(['XXX'])}
                                alignItems="flex-start"
                            >
                                <CustomTextField
                                    // style={{ width: 500 }}
                                    disableOnPermission
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'19.2'}
                                showTrueFalse
                                label={'Will the study site (hospital) receive reimbursement for the study?'}
                                trueFalseValue={getData('p4Reimburse')}
                                name={'p4Reimburse'}
                                onTrueFalseSelected={onFieldChange}
                                showDisableMessage
                                disableOnPermission
                                highlight={isHighlight(['p4Reimburse'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.2 Will the study site (hospital) receive reimbursement for the study?', 
                                    commentField: 'p4Reimburse',
                                    commentDetail: getCommentDetail("p4Reimburse", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'19.2.1'}
                                level={2}
                                highlight={isHighlight(['p4ReimburseFormat'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.2.1 If yes, state the format of reimbursement', 
                                    commentField: 'p4ReimburseFormat',
                                    commentDetail: getCommentDetail("p4ReimburseFormat", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p4ReimburseFormat')}
                                    name={'p4ReimburseFormat'}
                                    onChange={onFieldChange}
                                    label={'If yes, state the format of reimbursement'}
                                    disableOnPermission
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'19.2.2'}
                                level={2}
                                highlight={isHighlight(['p4ReimburseReason'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '19.2.2 If no, state the reason(s)', 
                                    commentField: 'p4ReimburseReason',
                                    commentDetail: getCommentDetail("p4ReimburseReason", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    label={'If no, state the reason(s)'}
                                    value={getData('p4ReimburseReason')}
                                    name={'p4ReimburseReason'}
                                    onChange={onFieldChange}
                                    disableOnPermission
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'20'}
                                bold
                                label={'Financial Costs and Payment to Subjects'}
                                fontSize={16}
                                style={{ marginTop: 30 }}
                            />
                            <NumberListFormItem
                                number={'20.1'}
                                // displayContentNextLine

                                labelTooltip={
                                    '“N/A” normally applies to retrospective study without direct subject recruitment.'
                                }
                                label={'Will the subjects be charged for the study article/service?'}
                                highlight={isHighlight(['p4Charged'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.1 Will the subjects be charged for the study article/service?', 
                                    commentField: 'p4Charged',
                                    commentDetail: getCommentDetail("p4Charged", allComments),
                                }}
                            >
                                <CustomCheckbox
                                    radioButton
                                    disableOnPermission={disableOnPermission}
                                    onAppFieldValidate={updateStudyValidation}
                                    defaultData={[
                                        { key: 'Y', value: false, display: 'Yes' },
                                        { key: 'N', value: false, display: 'No' },
                                        { key: 'A', value: false, display: 'N/A' },
                                    ]}
                                    name="p4Charged"
                                    onChange={(name, value) => {
                                        const { key } = value.find((item) => item.value);

                                        onFieldChange({
                                            target: {
                                                value: key,
                                                name,
                                            },
                                        });
                                    }}
                                    value={[getData('p4Charged')]}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'20.1.1'}
                                level={2}
                                highlight={isHighlight(['p4FinArrangement'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.1.1 If yes, state the financial arrangement', 
                                    commentField: 'p4FinArrangement',
                                    commentDetail: getCommentDetail("p4FinArrangement", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    disableOnPermission={disableOnPermission}
                                    label={'If yes, state the financial arrangement'}
                                    onAppFieldValidate={updateStudyValidation}
                                    value={getData('p4FinArrangement')}
                                    name={'p4FinArrangement'}
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                    onChange={onFieldChange}
                                    allowAction={getData('p4Charged') === 'Y'}
                                    updateApplicationFormOnDisableChange={formId}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'20.2'}
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                allowAction={isValueExistInField('p4Charged', ['Y', 'N'])}
                                updateApplicationFormOnDisableChange={formId}
                                displayContentNextLine
                                disableVerticalCenter
                                label={
                                    'Will the study article continue to be available to subjects after the study (if subjects benefited from it) until it is commercially available?'
                                }
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p4ConAvailable')}
                                name={'p4ConAvailable'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p4ConAvailable'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.2 Will the study article continue to be available to subjects after the study (if subjects benefited from it) until it is commercially available?', 
                                    commentField: 'p4ConAvailable',
                                    commentDetail: getCommentDetail("p4ConAvailable", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'20.2.1'}
                                level={2}
                                highlight={isHighlight(['p4PlanFinArrangement'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.2.1 If yes, state the planned financial arrangement', 
                                    commentField: 'p4PlanFinArrangement',
                                    commentDetail: getCommentDetail("p4PlanFinArrangement", allComments),
                                }}
                            >
                                <CustomTextField
                                    // allowEdit={true}
                                    value={getData('p4PlanFinArrangement')}
                                    debounceChange
                                    name={'p4PlanFinArrangement'}
                                    onChange={onFieldChange}
                                    disableOnPermission={disableOnPermission}
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                    label={'If yes, state the planned financial arrangement'}
                                    onAppFieldValidate={updateStudyValidation}
                                    allowAction={
                                        getData('p4ConAvailable') === true &&
                                        isValueExistInField('p4Charged', ['Y', 'N'])
                                    }
                                    updateApplicationFormOnDisableChange={formId}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'20.2.2'}
                                showTrueFalse
                                level={2}
                                label={'If no, will there be any impact on disease management of the study subject?'}
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p4ImpactDisease')}
                                name={'p4ImpactDisease'}
                                allowAction={
                                    getData('p4ConAvailable') === false && isValueExistInField('p4Charged', ['Y', 'N'])
                                }
                                updateApplicationFormOnDisableChange={formId}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p4ImpactDisease'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.2.2 If no, will there be any impact on disease management of the study subject?', 
                                    commentField: 'p4ImpactDisease',
                                    commentDetail: getCommentDetail("p4ImpactDisease", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'20.2.2.1'}
                                level={3}
                                highlight={isHighlight(['p4DiseaseArrangement'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.2.2.1 Provide arrangement', 
                                    commentField: 'p4DiseaseArrangement',
                                    commentDetail: getCommentDetail("p4DiseaseArrangement", allComments),
                                }}
                            >
                                <CustomTextField
                                    value={getData('p4DiseaseArrangement')}
                                    name={'p4DiseaseArrangement'}
                                    disableOnPermission={disableOnPermission}
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                    debounceChange
                                    onChange={onFieldChange}
                                    onAppFieldValidate={updateStudyValidation}
                                    label={'Provide arrangement'}
                                    allowAction={
                                        getData('p4ConAvailable') === false &&
                                        getData('p4ImpactDisease') === true &&
                                        isValueExistInField('p4Charged', ['Y', 'N'])
                                    }
                                    updateApplicationFormOnDisableChange={formId}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'20.3'}
                                showTrueFalse
                                labelWidth={400}
                                disableOnPermission={disableOnPermission}
                                label={'Does the consent form explain the above arrangement?'}
                                trueFalseValue={getData('p4Explain')}
                                onAppFieldValidate={updateStudyValidation}
                                name={'p4Explain'}
                                allowAction={isValueExistInField('p4Charged', ['Y', 'N'])}
                                updateApplicationFormOnDisableChange={formId}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p4Explain'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.3 Does the consent form explain the above arrangement?', 
                                    commentField: 'p4Explain',
                                    commentDetail: getCommentDetail("p4Explain", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'20.4'}
                                showTrueFalse
                                labelWidth={400}
                                disableOnPermission={disableOnPermission}
                                label={'Will subjects receive any material rewards (including payments)?'}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p4Rewards')}
                                name={'p4Rewards'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p4Rewards'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.4 Will subjects receive any material rewards (including payments)?', 
                                    commentField: 'p4Rewards',
                                    commentDetail: getCommentDetail("p4Rewards", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'20.4.1'}
                                label={`Reward nature`}
                                level={2}
                                highlight={isHighlight(['p4RewardsNature', 'p4RewardsOther'])}
                                style={{ width: '100%' }}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.4.1 Reward nature', 
                                    commentField: 'p4RewardsNature',
                                    commentDetail: getCommentDetail("p4RewardsNature", allComments),
                                }}
                            >
                                <CustomCheckbox
                                    allowAction={getData('p4Rewards') === true}
                                    onAppFieldValidate={updateStudyValidation}
                                    disableOnPermission={disableOnPermission}
                                    updateApplicationFormOnDisableChange={formId}
                                    name={'p4RewardsNature'}
                                    remarkParams={[
                                        {
                                            key: 'O',
                                            params: {
                                                style: { marginLeft: 15 },
                                                value: getData('p4RewardsOther'),
                                                onChange: onFieldChange,
                                                onAppFieldValidate: updateStudyValidation,
                                                disableOnPermission,
                                                debounceChange: true,
                                                name: 'p4RewardsOther',
                                                allowAction:
                                                    getData('p4Rewards') === true && getData('p4RewardsNature') === 'O',
                                                maxLength: 200,
                                                isTextArea: true,
                                                textAreaMinRow: 3,
                                                updateApplicationFormOnDisableChange: formId,
                                            },
                                            SRadioLayoutStyle: { flex: 1 },
                                        },
                                    ]}
                                    defaultData={[
                                        { key: 'C', value: false, display: 'Cash' },
                                        {
                                            key: 'O',
                                            value: false,
                                            display: 'Others',
                                            showRemarkInput: true,
                                            tooltip: 'E.g. gift certificate, supermarket coupon, etc.',
                                        },
                                    ]}
                                    value={[getData('p4RewardsNature')]}
                                    test
                                    onItemChange={(key, value) => {
                                        onFieldChange({
                                            target: {
                                                name: 'p4RewardsNature',
                                                value: key,
                                            },
                                        });
                                    }}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'20.4.2'}
                                level={2}
                                highlight={isHighlight(['p4RewardsAmount'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.4.2 Amount of payment (in HK$)', 
                                    commentField: 'p4RewardsAmount',
                                    commentDetail: getCommentDetail("p4RewardsAmount", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    style={{ width: 300 }}
                                    label={'Amount of payment (in HK$)'}
                                    value={getData('p4RewardsAmount')}
                                    onAppFieldValidate={updateStudyValidation}
                                    numberField
                                    debounceChange
                                    name={'p4RewardsAmount'}
                                    maxLength={1000}
                                    allowAction={getData('p4Rewards') === true}
                                    updateApplicationFormOnDisableChange={formId}
                                    onChange={onFieldChange}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'20.4.3'}
                                label={`Mode of payment`}
                                level={2}
                                highlight={isHighlight(['p4RewardsMode', 'p4RewardsSchedule'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '20.4.3 Mode of payment', 
                                    commentField: 'p4RewardsMode',
                                    commentDetail: getCommentDetail("p4RewardsMode", allComments),
                                }}
                            >
                                <CustomCheckbox
                                    disableOnPermission={disableOnPermission}
                                    allowAction={getData('p4Rewards') === true}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    remarkParams={[
                                        {
                                            key: 'S',
                                            params: {
                                                debounceChange: true,
                                                disableOnPermission,
                                                style: { marginLeft: 15 },
                                                value: getData('p4RewardsSchedule'),
                                                onChange: onFieldChange,
                                                onAppFieldValidate: updateStudyValidation,
                                                name: 'p4RewardsSchedule',
                                                test: true,
                                                allowAction:
                                                    getData('p4Rewards') === true && getData('p4RewardsMode') === 'S',
                                                updateApplicationFormOnDisableChange: formId,
                                                maxLength: 500,
                                            },
                                        },
                                    ]}
                                    defaultData={[
                                        { key: 'O', value: false, display: 'One-off' },
                                        {
                                            key: 'S',
                                            value: false,
                                            display: 'By schedule',
                                            showRemarkInput: true,
                                            tooltip: 'E.g. pay per visit',
                                        },
                                    ]}
                                    name="p4RewardsMode"
                                    value={[getData('p4RewardsMode')]}
                                    onItemChange={(key, value) => {
                                        onFieldChange({
                                            target: {
                                                name: 'p4RewardsMode',
                                                value: key,
                                            },
                                        });
                                    }}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'21'}
                                bold
                                label={'Research Organization and Indemnity'}
                                fontSize={16}
                                style={{ marginTop: 30 }}
                            />

                            <NumberListFormItem number={'21.1'} highlight={isHighlight(['p4Responsible'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '21.1 Research Organization and Indemnity', 
                                    commentField: 'p4Responsible',
                                    commentDetail: getCommentDetail("p4Responsible", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    label={'The organization / individual responsible for the study'}
                                    value={getData('p4Responsible')}
                                    name={'p4Responsible'}
                                    debounceChange
                                    maxLength={500}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'21.2'}
                                displayContentNextLine
                                label={'Collaborating parties that jointly take on the responsibilities for the study'}
                                highlight={isHighlight(['collaborating'], true)}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '21.2 Collaborating parties that jointly take on the responsibilities for the study', 
                                    commentStatus: null,
                                    commentField: 'collaborating',
                                    commentDetail: getCommentDetail("collaborating", allComments),
                                }}
                            >
                                <Grid container>
                                    <CustomButton
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        style={{ marginBottom: 8 }}
                                        onClick={handleCollaborateRowCreate}
                                    >
                                        Add
                                    </CustomButton>
                                    <CustomTable
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        fullWidthTable
                                        rows={collaborateRows}
                                        columns={collaborateColumns}
                                        onDelete={handleCollaborateDelete}
                                        onEdit={handleCollaborateEditClick}
                                        name={'collaborating'}
                                        disableOldDataDelete={isAmendment}
                                        // onAppFieldValidate={updateStudyValidation} // optional as requested by client
                                        isEditAllow
                                        isDeleteAllow
                                        applicationCreateDtm={applicationCreateDtm}
                                    />
                                </Grid>
                            </NumberListFormItem>

                            <NumberListFormItem number={'21.3'} label={'Indemnity'} />

                            <NumberListFormItem
                                number={'21.3.1'}
                                showTrueFalse
                                displayContentNextLine
                                level={2}
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                allowAction={getData('p4Sponsor') === true}
                                updateApplicationFormOnDisableChange={formId}
                                disableVerticalCenter
                                label={
                                    'For sponsored clinical research or where commercial interest is involved, will the sponsor indemnify study related claims?'
                                }
                                trueFalseValue={getData('p4Indemnify')}
                                name={'p4Indemnify'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p4Indemnify'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '21.3.1 For sponsored clinical research or where commercial interest is involved, will the sponsor indemnify study related claims?', 
                                    commentField: 'p4Indemnify',
                                    commentDetail: getCommentDetail("p4Indemnify", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'21.3.1.1'}
                                level={3}
                                highlight={isHighlight(['p4IndemnifyResponsible'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '21.3.1.1 If no, specify the reason and who will be responsible for the indemnity', 
                                    commentField: 'p4IndemnifyResponsible',
                                    commentDetail: getCommentDetail("p4IndemnifyResponsible", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    label={'If no, specify the reason and who will be responsible for the indemnity'}
                                    value={getData('p4IndemnifyResponsible')}
                                    onAppFieldValidate={updateStudyValidation}
                                    name={'p4IndemnifyResponsible'}
                                    debounceChange
                                    maxLength={500}
                                    allowAction={getData('p4Sponsor') === true && getData('p4Indemnify') === false}
                                    updateApplicationFormOnDisableChange={formId}
                                    onChange={onFieldChange}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'21.3.1.2'}
                                showTrueFalse
                                level={3}
                                disableOnPermission={disableOnPermission}
                                label={'Is the indemnity agreement based on the HA approved form?'}
                                trueFalseValue={getData('p4IndemnifyBaseOnHA')}
                                onAppFieldValidate={updateStudyValidation}
                                name={'p4IndemnifyBaseOnHA'}
                                onTrueFalseSelected={onFieldChange}
                                allowAction={getData('p4Sponsor') === true && getData('p4Indemnify') === true}
                                updateApplicationFormOnDisableChange={formId}
                                highlight={isHighlight(['p4IndemnifyBaseOnHA'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '21.3.1.2 Is the indemnity agreement based on the HA approved form?', 
                                    commentField: 'p4IndemnifyBaseOnHA',
                                    commentDetail: getCommentDetail("p4IndemnifyBaseOnHA", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'21.4'}
                                showTrueFalse
                                label={'Will an insurance policy be arranged for the study?'}
                                // disableOnPermission={disableOnPermission || isAmendment}
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p4Insurance')}
                                name={'p4Insurance'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p4Insurance'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '21.4 Will an insurance policy be arranged for the study?', 
                                    commentField: 'p4Insurance',
                                    commentDetail: getCommentDetail("p4Insurance", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'21.5'}
                                displayContentNextLine
                                label={`Will a Clinical Trial Agreement or other legal agreement/contract(s) be signed with the sponsor/supporting party?`}
                                highlight={isHighlight(['p4AgreementSigned'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '21.5 Will a Clinical Trial Agreement or other legal agreement/contract(s) be signed with the sponsor/supporting party?', 
                                    commentField: 'p4AgreementSigned',
                                    commentDetail: getCommentDetail("p4AgreementSigned", allComments),
                                }}
                            >
                                <CustomCheckbox
                                    disableOnPermission={disableOnPermission}
                                    defaultData={[
                                        { key: 0, value: false, display: 'Yes, with HA' },
                                        { key: 1, value: false, display: 'Yes, with University' },
                                        { key: 2, value: false, display: 'No' },
                                    ]}
                                    value={[getData('p4AgreementSigned')]}
                                    name={'p4AgreementSigned'}
                                    onAppFieldValidate={updateStudyValidation}
                                    onItemChange={(key, value) => {
                                        setPostData(
                                            produce((draft) => {
                                                draft['p4AgreementSigned'] = key;
                                            }),
                                        );

                                        onFormUpdate('p4AgreementSigned', key);
                                    }}
                                />
                            </NumberListFormItem>
                        </>
                    )}
                </SLayout>
            </Paper>
        </SPaperLayout>
    );
}
